import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface AnalyticsData {
  overview: {
    totalSessions: number;
    averageScore: number;
    completionRate: number;
    activeUsers: number;
  };
  trackPerformance: Array<{
    trackId: string;
    trackName: string;
    totalSessions: number;
    averageScore: number;
    completionRate: number;
  }>;
  leaderboard: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    totalSessions: number;
    averageScore: number;
    bestScore: number;
  }>;
}

const TrainingAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get('/training/admin/analytics/overview');

      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      setError(error.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (!analytics || !analytics.overview) {
    return (
      <Alert severity="info">No analytics data available</Alert>
    );
  }

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color, fontSize: 48 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Overview Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Sessions"
            value={analytics.overview.totalSessions}
            icon={<SchoolIcon fontSize="inherit" />}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Average Score"
            value={`${analytics.overview.averageScore}%`}
            icon={<AssessmentIcon fontSize="inherit" />}
            color="#2e7d32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Completion Rate"
            value={`${analytics.overview.completionRate}%`}
            icon={<TrendingUpIcon fontSize="inherit" />}
            color="#ed6c02"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Users"
            value={analytics.overview.activeUsers}
            icon={<PeopleIcon fontSize="inherit" />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Track Performance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Track Performance
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Track Name</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Completion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!analytics.trackPerformance || analytics.trackPerformance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No track data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics.trackPerformance.map((track) => (
                      <TableRow key={track.trackId}>
                        <TableCell>{track.trackName}</TableCell>
                        <TableCell align="right">{track.totalSessions}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${track.averageScore}%`}
                            size="small"
                            color={track.averageScore >= 70 ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="right">{track.completionRate}%</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Leaderboard */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performers
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell align="right">Best Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!analytics.leaderboard || analytics.leaderboard.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No user data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics.leaderboard.slice(0, 10).map((user, index) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color={index === 0 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{user.userName}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{user.totalSessions}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${user.bestScore}%`}
                            size="small"
                            color={user.bestScore >= 80 ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrainingAnalytics;
